/**
 * AddonEditorDialog — ADDONS-2.
 *
 * Create + edit add-on items (nail trim, take-home shampoo, late checkout).
 * Cloned-down from OfferingEditorDialog: no tier ladder, no refund policy,
 * no e-invoice toggle, no transferability — add-ons are simpler.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'

import {
  billing,
  type AddonService,
  type AddonServiceCreate,
  type AddonServiceUpdate,
} from '../api/client'
import { Button } from '../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Switch } from '../components/ui/switch'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** null = create mode; an AddonService = edit mode. */
  existing: AddonService | null
  onSaved: () => void
}

type ServiceType = 'boarding' | 'daycare' | 'grooming'
const SERVICE_TYPES: ServiceType[] = ['boarding', 'daycare', 'grooming']
const CATEGORIES = ['grooming', 'wellness', 'retail', 'other']
const PRICING_TYPES = ['per_session', 'per_night', 'one_time']

export function AddonEditorDialog({
  open,
  onOpenChange,
  existing,
  onSaved,
}: Props) {
  const { t } = useTranslation()
  const isEdit = existing !== null

  const [name, setName] = useState('')
  const [nameZh, setNameZh] = useState('')
  const [category, setCategory] = useState<string>('other')
  const [price, setPrice] = useState<string>('')
  const [pricingType, setPricingType] = useState<string>('per_session')
  const [duration, setDuration] = useState<string>('')
  const [applicable, setApplicable] = useState<ServiceType[]>([
    'boarding',
    'daycare',
  ])
  const [active, setActive] = useState(true)
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    if (existing) {
      setName(existing.name)
      setNameZh(existing.name_zh)
      setCategory(existing.category || 'other')
      setPrice(String(existing.price ?? ''))
      setPricingType(existing.pricing_type || 'per_session')
      setDuration(
        existing.duration_minutes != null ? String(existing.duration_minutes) : '',
      )
      setApplicable(
        (existing.applicable_service_types as ServiceType[]) ?? [
          'boarding',
          'daycare',
        ],
      )
      setActive(existing.is_active)
      setNotes(existing.notes ?? '')
    } else {
      setName('')
      setNameZh('')
      setCategory('other')
      setPrice('')
      setPricingType('per_session')
      setDuration('')
      setApplicable(['boarding', 'daycare'])
      setActive(true)
      setNotes('')
    }
    setError(null)
  }, [open, existing])

  function toggleApplicable(s: ServiceType) {
    setApplicable((cur) =>
      cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s],
    )
  }

  async function submit() {
    if (!name.trim() || !nameZh.trim()) {
      setError('required')
      return
    }
    const priceNum = Number(price)
    if (!Number.isFinite(priceNum) || priceNum <= 0) {
      setError('price')
      return
    }
    if (applicable.length === 0) {
      setError('mustPickAtLeastOneService')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const body: AddonServiceCreate | AddonServiceUpdate = {
        name: name.trim(),
        name_zh: nameZh.trim(),
        category,
        price: priceNum.toFixed(2),
        pricing_type: pricingType,
        duration_minutes: duration ? Number(duration) : null,
        applicable_service_types: applicable,
        is_active: active,
        notes: notes.trim() || null,
      }
      if (isEdit && existing) {
        await billing.updateAddOn(existing.id, body)
      } else {
        await billing.createAddOn(body as AddonServiceCreate)
      }
      onSaved()
    } catch (e) {
      const detail = (e as { detail?: string; message?: string })
      setError(detail.detail ?? detail.message ?? 'save_failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md max-h-[90vh] overflow-y-auto"
        data-testid="addon-editor"
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t('packages.addOns.editor.editTitle')
              : t('packages.addOns.editor.createTitle')}
          </DialogTitle>
          <DialogDescription>{t('packages.addOns.subtitle')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="addon-name-zh">
                {t('packages.addOns.editor.nameZhLabel')}
              </Label>
              <Input
                id="addon-name-zh"
                value={nameZh}
                onChange={(e) => setNameZh(e.target.value)}
                placeholder="剪指甲"
                data-testid="addon-name-zh"
              />
            </div>
            <div>
              <Label htmlFor="addon-name">
                {t('packages.addOns.editor.nameLabel')}
              </Label>
              <Input
                id="addon-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nail trim"
                data-testid="addon-name"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="addon-price">
                {t('packages.addOns.editor.priceLabel')}
              </Label>
              <Input
                id="addon-price"
                type="number"
                inputMode="decimal"
                min="0"
                step="1"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="200"
                data-testid="addon-price"
              />
            </div>
            <div>
              <Label htmlFor="addon-pricing-type">
                {t('packages.addOns.editor.pricingTypeLabel')}
              </Label>
              <select
                id="addon-pricing-type"
                value={pricingType}
                onChange={(e) => setPricingType(e.target.value)}
                className="h-9 w-full rounded border px-2 text-sm"
                data-testid="addon-pricing-type"
              >
                {PRICING_TYPES.map((p) => (
                  <option key={p} value={p}>
                    {t(`packages.addOns.editor.pricingType.${p}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="addon-category">
                {t('packages.addOns.editor.categoryLabel')}
              </Label>
              <select
                id="addon-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="h-9 w-full rounded border px-2 text-sm"
                data-testid="addon-category"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {t(`packages.addOns.category.${c}`)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="addon-duration">
                {t('packages.addOns.editor.durationLabel')}
              </Label>
              <Input
                id="addon-duration"
                type="number"
                inputMode="numeric"
                min="0"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                placeholder="10"
                data-testid="addon-duration"
              />
            </div>
          </div>

          <div>
            <Label>{t('packages.addOns.editor.applicableLabel')}</Label>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {SERVICE_TYPES.map((s) => {
                const on = applicable.includes(s)
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleApplicable(s)}
                    className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                      on
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-input bg-muted/40 text-muted-foreground hover:bg-muted'
                    }`}
                    data-testid={`addon-applicable-${s}`}
                  >
                    {t(`packages.addOns.service.${s}`)}
                  </button>
                )
              })}
            </div>
          </div>

          {category === 'retail' && (
            <p className="rounded bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
              {t('packages.addOns.editor.retailInventoryHint')}
            </p>
          )}

          <div>
            <Label htmlFor="addon-notes">
              {t('packages.addOns.editor.notesLabel')}
            </Label>
            <Input
              id="addon-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="addon-notes"
            />
          </div>

          <div className="flex items-center justify-between rounded border bg-muted/30 p-2">
            <Label htmlFor="addon-active" className="text-sm">
              {t('packages.addOns.editor.activeLabel')}
            </Label>
            <Switch
              id="addon-active"
              checked={active}
              onCheckedChange={setActive}
              data-testid="addon-active"
            />
          </div>

          {error && (
            <p className="text-sm text-destructive" data-testid="addon-error">
              {error === 'required' && t('packages.addOns.editor.required')}
              {error === 'price' &&
                t('packages.addOns.editor.priceMustBePositive')}
              {error === 'mustPickAtLeastOneService' &&
                t('packages.addOns.editor.mustPickAtLeastOneService')}
              {!['required', 'price', 'mustPickAtLeastOneService'].includes(
                error,
              ) && error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            {t('packages.addOns.editor.cancel')}
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            data-testid="addon-submit"
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('packages.addOns.editor.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
